import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send, User, Loader2, X, MessageSquare, TrendingUp, Users, Store,
  Plus, ChevronLeft, Trash2, Edit2, Check, Sparkles, Search, CreditCard,
  ShoppingCart, Settings, Shield, FileText, DollarSign, Package,
  AlertTriangle, CheckCircle, XCircle, Clock, ChevronRight, Menu,
  Building, UserCheck, Ban, RefreshCw, Zap, BarChart3, Hash, AtSign
} from 'lucide-react';
import './AIAssistant.css';

// Platform default currency for aggregate stats
const PLATFORM_CURRENCY = 'NGN';
const PLATFORM_SYMBOL = '₦';

const formatPlatformMoney = (amount) => {
  if (!amount && amount !== 0) return `${PLATFORM_SYMBOL}0`;
  return `${PLATFORM_SYMBOL}${Number(amount).toLocaleString()}`;
};

const AIAssistant = () => {
  // Conversation management state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  // Command state
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [commandSearch, setCommandSearch] = useState('');

  // Autocomplete state
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteType, setAutocompleteType] = useState(null); // 'merchant', 'subscription', 'order', 'customer'
  const [autocompleteItems, setAutocompleteItems] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [selectedAutocompleteIndex, setSelectedAutocompleteIndex] = useState(0);

  // Action state
  const [selectedEntity, setSelectedEntity] = useState(null); // { type: 'merchant'|'subscription', data: {...} }
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Platform stats
  const [platformStats, setPlatformStats] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const autocompleteDebounceRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations and stats on mount
  useEffect(() => {
    loadConversations();
    loadPlatformStats();
  }, []);

  // Command palette commands
  const commands = [
    { id: 'merchant-search', name: 'Search Merchants', shortcut: '@', icon: Store, category: 'Search', description: 'Find and manage merchants' },
    { id: 'subscription-search', name: 'Search Subscriptions', shortcut: '#', icon: CreditCard, category: 'Search', description: 'Find and manage subscriptions' },
    { id: 'order-search', name: 'Search Orders', shortcut: '/orders', icon: ShoppingCart, category: 'Search', description: 'Find orders across platform' },
    { id: 'customer-search', name: 'Search Customers', shortcut: '/customers', icon: Users, category: 'Search', description: 'Find customers' },
    { id: 'pending-kyc', name: 'Pending KYC Reviews', shortcut: '/kyc', icon: Shield, category: 'Actions', description: 'View pending KYC applications' },
    { id: 'pending-settlements', name: 'Pending Settlements', shortcut: '/settlements', icon: DollarSign, category: 'Actions', description: 'View pending settlements' },
    { id: 'pending-refunds', name: 'Pending Refunds', shortcut: '/refunds', icon: RefreshCw, category: 'Actions', description: 'View pending refund requests' },
    { id: 'expiring-subscriptions', name: 'Expiring Subscriptions', shortcut: '/expiring', icon: Clock, category: 'Reports', description: 'Subscriptions expiring soon' },
    { id: 'revenue-report', name: 'Revenue Summary', shortcut: '/revenue', icon: TrendingUp, category: 'Reports', description: 'Platform revenue overview' },
    { id: 'merchant-report', name: 'Merchant Performance', shortcut: '/performance', icon: BarChart3, category: 'Reports', description: 'Top performing merchants' },
  ];

  // Suggested queries
  const suggestedQueries = [
    { icon: Users, text: 'How many active merchants do we have?', color: '#6366f1' },
    { icon: TrendingUp, text: 'What is our total revenue this month?', color: '#10b981' },
    { icon: Clock, text: 'Show subscriptions expiring this week', color: '#f59e0b' },
    { icon: Shield, text: 'Are there any pending KYC reviews?', color: '#ef4444' },
    { icon: DollarSign, text: 'What settlements need approval?', color: '#8b5cf6' },
    { icon: BarChart3, text: 'Who are our top performing merchants?', color: '#06b6d4' },
  ];

  // API Functions
  const loadConversations = async () => {
    try {
      const res = await fetch('/api/ai/conversations?userType=ADMIN', {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const loadPlatformStats = async () => {
    try {
      const res = await fetch('/api/ai/admin/platform-stats', {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setPlatformStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading platform stats:', error);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const res = await fetch(`/api/ai/conversations/${conversationId}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success && data.conversation) {
        const loadedMessages = data.conversation.messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.createdAt),
          stats: msg.metadata?.stats,
          suggestedActions: msg.metadata?.suggestedActions
        }));
        setMessages(loadedMessages);
        setActiveConversationId(conversationId);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const createConversation = async () => {
    try {
      const res = await fetch('/api/ai/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userType: 'ADMIN' })
      });
      const data = await res.json();
      if (data.success && data.conversation) {
        setConversations(prev => [data.conversation, ...prev]);
        setActiveConversationId(data.conversation.id);
        setMessages([]);
        return data.conversation.id;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
    return null;
  };

  const saveMessage = async (conversationId, role, content, metadata = null) => {
    try {
      await fetch(`/api/ai/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role, content, metadata })
      });
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      await fetch(`/api/ai/conversations/${conversationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const renameConversation = async (conversationId, title) => {
    try {
      await fetch(`/api/ai/conversations/${conversationId}/title`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title })
      });
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, title } : c
      ));
    } catch (error) {
      console.error('Error renaming conversation:', error);
    }
  };

  // Autocomplete search functions
  const searchMerchants = async (query) => {
    setAutocompleteLoading(true);
    try {
      const res = await fetch(`/api/ai/admin/merchants/search?query=${encodeURIComponent(query)}&limit=8`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setAutocompleteItems(data.merchants);
      }
    } catch (error) {
      console.error('Error searching merchants:', error);
    } finally {
      setAutocompleteLoading(false);
    }
  };

  const searchSubscriptions = async (query) => {
    setAutocompleteLoading(true);
    try {
      const res = await fetch(`/api/ai/admin/subscriptions/search?query=${encodeURIComponent(query)}&limit=8`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setAutocompleteItems(data.subscriptions);
      }
    } catch (error) {
      console.error('Error searching subscriptions:', error);
    } finally {
      setAutocompleteLoading(false);
    }
  };

  const searchOrders = async (query) => {
    setAutocompleteLoading(true);
    try {
      const res = await fetch(`/api/ai/admin/orders/search?query=${encodeURIComponent(query)}&limit=8`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setAutocompleteItems(data.orders);
      }
    } catch (error) {
      console.error('Error searching orders:', error);
    } finally {
      setAutocompleteLoading(false);
    }
  };

  const searchCustomers = async (query) => {
    setAutocompleteLoading(true);
    try {
      const res = await fetch(`/api/ai/admin/customers/search?query=${encodeURIComponent(query)}&limit=8`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setAutocompleteItems(data.customers);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setAutocompleteLoading(false);
    }
  };

  // Execute admin action
  const executeAction = async (actionType, targetId, targetType, params = {}) => {
    setActionLoading(true);
    try {
      const res = await fetch('/api/ai/admin/execute-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ actionType, targetId, targetType, params })
      });
      const data = await res.json();

      if (data.success) {
        // Add success message to chat
        const actionMessage = {
          role: 'assistant',
          content: `✅ ${data.message}`,
          timestamp: new Date(),
          isActionResult: true
        };
        setMessages(prev => [...prev, actionMessage]);

        // Refresh stats
        loadPlatformStats();

        // Clear selection
        setSelectedEntity(null);
        setShowActionPanel(false);
      } else {
        const errorMessage = {
          role: 'assistant',
          content: `❌ Action failed: ${data.error}`,
          timestamp: new Date(),
          isActionResult: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error executing action:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    // Clear debounce
    if (autocompleteDebounceRef.current) {
      clearTimeout(autocompleteDebounceRef.current);
    }

    // Detect @ trigger for merchants
    if (value.includes('@')) {
      const atIndex = value.lastIndexOf('@');
      const query = value.slice(atIndex + 1).split(' ')[0];
      setAutocompleteType('merchant');
      setShowAutocomplete(true);
      setSelectedAutocompleteIndex(0);

      autocompleteDebounceRef.current = setTimeout(() => {
        searchMerchants(query);
      }, 300);
    }
    // Detect # trigger for subscriptions
    else if (value.includes('#')) {
      const hashIndex = value.lastIndexOf('#');
      const query = value.slice(hashIndex + 1).split(' ')[0];
      setAutocompleteType('subscription');
      setShowAutocomplete(true);
      setSelectedAutocompleteIndex(0);

      autocompleteDebounceRef.current = setTimeout(() => {
        searchSubscriptions(query);
      }, 300);
    }
    // Detect / trigger for command palette
    else if (value === '/' || (value.startsWith('/') && !value.includes(' '))) {
      setShowCommandPalette(true);
      setCommandSearch(value.slice(1));
    }
    else {
      setShowAutocomplete(false);
      setShowCommandPalette(false);
    }
  };

  // Handle autocomplete selection
  const handleAutocompleteSelect = (item) => {
    let newInput = input;

    if (autocompleteType === 'merchant') {
      const atIndex = input.lastIndexOf('@');
      newInput = input.slice(0, atIndex) + `@${item.storeName} `;
      setSelectedEntity({ type: 'merchant', data: item });
      setShowActionPanel(true);
    } else if (autocompleteType === 'subscription') {
      const hashIndex = input.lastIndexOf('#');
      newInput = input.slice(0, hashIndex) + `#${item.storeName || item.id.slice(-8)} `;
      setSelectedEntity({ type: 'subscription', data: item });
      setShowActionPanel(true);
    }

    setInput(newInput);
    setShowAutocomplete(false);
    inputRef.current?.focus();
  };

  // Handle command selection
  const handleCommandSelect = (command) => {
    setShowCommandPalette(false);

    if (command.shortcut === '@') {
      setInput('@');
      setAutocompleteType('merchant');
      setShowAutocomplete(true);
      searchMerchants('');
    } else if (command.shortcut === '#') {
      setInput('#');
      setAutocompleteType('subscription');
      setShowAutocomplete(true);
      searchSubscriptions('');
    } else {
      // Execute as a query
      handleSend(command.name);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (showAutocomplete && autocompleteItems.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedAutocompleteIndex(prev =>
          prev < autocompleteItems.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedAutocompleteIndex(prev =>
          prev > 0 ? prev - 1 : autocompleteItems.length - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleAutocompleteSelect(autocompleteItems[selectedAutocompleteIndex]);
      } else if (e.key === 'Escape') {
        setShowAutocomplete(false);
      }
    } else if (showCommandPalette) {
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
        setInput('');
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle new conversation
  const handleNewConversation = () => {
    setActiveConversationId(null);
    setMessages([]);
    setSelectedEntity(null);
    setShowActionPanel(false);
    inputRef.current?.focus();
  };

  // Handle send message
  const handleSend = async (query = input) => {
    if (!query.trim() || loading) return;

    let convId = activeConversationId;

    if (!convId) {
      convId = await createConversation();
      if (!convId) return;
    }

    const userMessage = {
      role: 'user',
      content: query,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setShowAutocomplete(false);
    setShowCommandPalette(false);

    // Save user message
    await saveMessage(convId, 'user', query);

    // Update conversation title if first message
    const currentConv = conversations.find(c => c.id === convId);
    if (!currentConv?.title) {
      const title = query.slice(0, 50) + (query.length > 50 ? '...' : '');
      await renameConversation(convId, title);
    }

    try {
      const context = selectedEntity ? {
        selectedMerchant: selectedEntity.type === 'merchant' ? selectedEntity.data : null,
        selectedSubscription: selectedEntity.type === 'subscription' ? selectedEntity.data : null
      } : null;

      const res = await fetch('/api/ai/admin/enhanced-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query, sessionId: convId, context })
      });

      const data = await res.json();

      const assistantMessage = {
        role: 'assistant',
        content: data.success ? data.response : `Error: ${data.error || 'Unknown error occurred'}`,
        timestamp: new Date(),
        stats: data.stats,
        suggestedActions: data.suggestedActions
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save assistant message
      await saveMessage(convId, 'assistant', assistantMessage.content, {
        stats: data.stats,
        suggestedActions: data.suggestedActions
      });

      // Refresh conversations list
      loadConversations();
    } catch (error) {
      console.error('AI chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: `Connection error: ${error.message}. Please check your connection.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      await saveMessage(convId, 'assistant', errorMessage.content);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestedQuery = (query) => {
    setInput(query);
    handleSend(query);
  };

  const handleQuickAction = (trigger) => {
    if (trigger === '@') {
      setInput('@');
      setAutocompleteType('merchant');
      setShowAutocomplete(true);
      searchMerchants('');
    } else if (trigger === '#') {
      setInput('#');
      setAutocompleteType('subscription');
      setShowAutocomplete(true);
      searchSubscriptions('');
    }
    inputRef.current?.focus();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleStartEdit = (conv) => {
    setEditingId(conv.id);
    setEditTitle(conv.title || 'New conversation');
  };

  const handleSaveEdit = (id) => {
    if (editTitle.trim()) {
      renameConversation(id, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle('');
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: '#10b981',
      APPROVED: '#10b981',
      PENDING: '#f59e0b',
      SUSPENDED: '#ef4444',
      CANCELLED: '#6b7280',
      EXPIRED: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  // Filter commands for palette
  const filteredCommands = commands.filter(cmd =>
    cmd.name.toLowerCase().includes(commandSearch.toLowerCase()) ||
    cmd.shortcut.toLowerCase().includes(commandSearch.toLowerCase())
  );

  // Group commands by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  return (
    <div className="admin-ai-layout">
      {/* Conversation Sidebar */}
      <div className={`admin-ai-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {sidebarCollapsed ? (
          <>
            <button
              className="admin-sidebar-toggle"
              onClick={() => setSidebarCollapsed(false)}
              title="Expand sidebar"
            >
              <MessageSquare size={20} />
            </button>
            <button
              className="admin-sidebar-toggle"
              onClick={handleNewConversation}
              title="New conversation"
            >
              <Plus size={20} />
            </button>
            {/* Collapsed conversation dots */}
            <div className="admin-collapsed-convs">
              {conversations.slice(0, 5).map((conv, idx) => (
                <button
                  key={conv.id}
                  className={`admin-collapsed-conv ${activeConversationId === conv.id ? 'active' : ''}`}
                  onClick={() => loadConversation(conv.id)}
                  title={conv.title || 'New conversation'}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="admin-sidebar-header">
              <span>History</span>
              <button
                className="admin-sidebar-collapse"
                onClick={() => setSidebarCollapsed(true)}
              >
                <ChevronLeft size={18} />
              </button>
            </div>

            <button
              className="admin-new-chat-btn"
              onClick={handleNewConversation}
            >
              <Plus size={18} />
              <span>New Chat</span>
            </button>

            <div className="admin-conversations-list">
              {conversationsLoading ? (
                <div className="admin-sidebar-loading">
                  <Loader2 className="admin-spinner" size={16} />
                </div>
              ) : conversations.length === 0 ? (
                <div className="admin-sidebar-empty">
                  <MessageSquare size={20} />
                  <span>No conversations yet</span>
                </div>
              ) : (
                conversations.map(conv => (
                  <div
                    key={conv.id}
                    className={`admin-conv-item ${activeConversationId === conv.id ? 'active' : ''}`}
                  >
                    {editingId === conv.id ? (
                      <div className="admin-conv-edit">
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit(conv.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          autoFocus
                        />
                        <button onClick={() => handleSaveEdit(conv.id)}>
                          <Check size={14} />
                        </button>
                        <button onClick={() => setEditingId(null)}>
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div
                          className="admin-conv-content"
                          onClick={() => loadConversation(conv.id)}
                        >
                          <div className="admin-conv-title">
                            {conv.title || 'New conversation'}
                          </div>
                          <div className="admin-conv-date">
                            {formatDate(conv.updatedAt)}
                          </div>
                        </div>
                        <div className="admin-conv-actions">
                          <button onClick={() => handleStartEdit(conv)} title="Rename">
                            <Edit2 size={12} />
                          </button>
                          <button
                            onClick={() => deleteConversation(conv.id)}
                            title="Delete"
                            className="delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="admin-ai-main">
        {/* Header */}
        <div className="admin-ai-header">
          <div className="admin-header-left">
            <button
              className="admin-mobile-menu"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              <Menu size={20} />
            </button>
            <div className="admin-header-info">
              <div className="admin-header-icon">
                <Sparkles size={20} />
              </div>
              <div>
                <h2>Admin AI Assistant</h2>
                <p>Use @ for merchants, # for subscriptions, / for commands</p>
              </div>
            </div>
          </div>
          {platformStats && (
            <div className="admin-header-stats">
              <div className="admin-header-stat">
                <span className="stat-value">{platformStats.merchants?.active || 0}</span>
                <span className="stat-label">Active</span>
              </div>
              <div className="admin-header-stat pending">
                <span className="stat-value">{platformStats.pendingActions?.kyc || 0}</span>
                <span className="stat-label">KYC</span>
              </div>
              <div className="admin-header-stat warning">
                <span className="stat-value">{platformStats.subscriptions?.expiringSoon || 0}</span>
                <span className="stat-label">Expiring</span>
              </div>
            </div>
          )}
        </div>

        {/* Chat Messages */}
        <div className="admin-ai-chat">
          {messages.length === 0 ? (
            <div className="admin-ai-welcome">
              <div className="admin-welcome-icon">
                <Sparkles size={40} />
              </div>
              <h2>Hi, I'm Reva - Your Admin Assistant</h2>
              <p>I can help you manage merchants, subscriptions, orders, and platform analytics. Use the shortcuts below or just ask me anything.</p>

              {/* Quick Actions */}
              <div className="admin-quick-actions">
                <button onClick={() => handleQuickAction('@')} className="admin-quick-btn merchant">
                  <AtSign size={16} />
                  <span>@ Merchants</span>
                </button>
                <button onClick={() => handleQuickAction('#')} className="admin-quick-btn subscription">
                  <Hash size={16} />
                  <span># Subscriptions</span>
                </button>
                <button onClick={() => { setInput('/'); setShowCommandPalette(true); }} className="admin-quick-btn command">
                  <Zap size={16} />
                  <span>/ Commands</span>
                </button>
              </div>

              {/* Suggested Queries */}
              <div className="admin-suggestions">
                <p className="admin-suggestions-label">Try asking:</p>
                <div className="admin-suggestions-grid">
                  {suggestedQueries.map((query, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestedQuery(query.text)}
                      className="admin-suggestion-btn"
                      disabled={loading}
                      style={{ '--accent-color': query.color }}
                    >
                      <query.icon size={16} />
                      <span>{query.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="admin-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className={`admin-message admin-message-${msg.role}`}>
                  <div className="admin-message-avatar">
                    {msg.role === 'assistant' ? (
                      <Sparkles size={18} />
                    ) : (
                      <User size={18} />
                    )}
                  </div>
                  <div className="admin-message-body">
                    <div className="admin-message-text">{msg.content}</div>

                    {/* Stats cards */}
                    {msg.stats && (
                      <div className="admin-stats-grid">
                        <div className="admin-stat-card">
                          <Store size={14} />
                          <span className="admin-stat-value">{msg.stats.totalMerchants}</span>
                          <span className="admin-stat-label">Merchants</span>
                        </div>
                        <div className="admin-stat-card">
                          <Users size={14} />
                          <span className="admin-stat-value">{msg.stats.totalCustomers}</span>
                          <span className="admin-stat-label">Customers</span>
                        </div>
                        <div className="admin-stat-card">
                          <ShoppingCart size={14} />
                          <span className="admin-stat-value">{msg.stats.totalOrders}</span>
                          <span className="admin-stat-label">Orders</span>
                        </div>
                        <div className="admin-stat-card">
                          <DollarSign size={14} />
                          <span className="admin-stat-value">{formatPlatformMoney(msg.stats.totalRevenue)}</span>
                          <span className="admin-stat-label">Revenue</span>
                        </div>
                      </div>
                    )}

                    {/* Suggested Actions */}
                    {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                      <div className="admin-suggested-actions">
                        <span className="actions-label">Quick Actions:</span>
                        {msg.suggestedActions.map((action, i) => (
                          <button
                            key={i}
                            className="admin-action-chip"
                            onClick={() => executeAction(
                              action.action,
                              selectedEntity?.data?.id,
                              selectedEntity?.type?.toUpperCase()
                            )}
                            disabled={actionLoading}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="admin-message-time">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="admin-message admin-message-assistant">
                  <div className="admin-message-avatar">
                    <Sparkles size={18} />
                  </div>
                  <div className="admin-message-body">
                    <div className="admin-loading">
                      <Loader2 className="admin-spinner" size={16} />
                      <span>Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Selected Entity Panel */}
        {selectedEntity && showActionPanel && (
          <div className="admin-entity-panel">
            <div className="admin-entity-header">
              <div className="admin-entity-info">
                {selectedEntity.type === 'merchant' ? (
                  <>
                    <Store size={16} />
                    <span>{selectedEntity.data.storeName}</span>
                    <span
                      className="admin-entity-status"
                      style={{ background: getStatusColor(selectedEntity.data.status) }}
                    >
                      {selectedEntity.data.status}
                    </span>
                  </>
                ) : (
                  <>
                    <CreditCard size={16} />
                    <span>{selectedEntity.data.storeName || selectedEntity.data.id.slice(-8)}</span>
                    <span
                      className="admin-entity-status"
                      style={{ background: getStatusColor(selectedEntity.data.status) }}
                    >
                      {selectedEntity.data.status}
                    </span>
                  </>
                )}
              </div>
              <button
                className="admin-entity-close"
                onClick={() => { setSelectedEntity(null); setShowActionPanel(false); }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="admin-entity-actions">
              {selectedEntity.type === 'merchant' && (
                <>
                  {selectedEntity.data.status === 'ACTIVE' && (
                    <button
                      className="admin-entity-action danger"
                      onClick={() => executeAction('SUSPEND_MERCHANT', selectedEntity.data.id, 'MERCHANT')}
                      disabled={actionLoading}
                    >
                      <Ban size={14} />
                      Suspend
                    </button>
                  )}
                  {selectedEntity.data.status === 'SUSPENDED' && (
                    <button
                      className="admin-entity-action success"
                      onClick={() => executeAction('ACTIVATE_MERCHANT', selectedEntity.data.id, 'MERCHANT')}
                      disabled={actionLoading}
                    >
                      <CheckCircle size={14} />
                      Activate
                    </button>
                  )}
                  {selectedEntity.data.status === 'PENDING' && (
                    <button
                      className="admin-entity-action success"
                      onClick={() => executeAction('APPROVE_MERCHANT', selectedEntity.data.id, 'MERCHANT')}
                      disabled={actionLoading}
                    >
                      <UserCheck size={14} />
                      Approve
                    </button>
                  )}
                </>
              )}
              {selectedEntity.type === 'subscription' && (
                <>
                  {selectedEntity.data.status === 'ACTIVE' && (
                    <>
                      <button
                        className="admin-entity-action primary"
                        onClick={() => executeAction('EXTEND_SUBSCRIPTION', selectedEntity.data.id, 'SUBSCRIPTION', { days: 30 })}
                        disabled={actionLoading}
                      >
                        <Clock size={14} />
                        Extend 30 Days
                      </button>
                      <button
                        className="admin-entity-action danger"
                        onClick={() => executeAction('CANCEL_SUBSCRIPTION', selectedEntity.data.id, 'SUBSCRIPTION')}
                        disabled={actionLoading}
                      >
                        <XCircle size={14} />
                        Cancel
                      </button>
                    </>
                  )}
                  {(selectedEntity.data.status === 'EXPIRED' || selectedEntity.data.status === 'CANCELLED') && (
                    <button
                      className="admin-entity-action success"
                      onClick={() => executeAction('EXTEND_SUBSCRIPTION', selectedEntity.data.id, 'SUBSCRIPTION', { days: 30 })}
                      disabled={actionLoading}
                    >
                      <RefreshCw size={14} />
                      Reactivate
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="admin-ai-input-area">
          <div className="admin-input-wrapper">
            {/* Command Palette */}
            {showCommandPalette && (
              <div className="admin-command-palette">
                <div className="admin-palette-header">
                  <span>Commands</span>
                  <kbd>ESC</kbd>
                </div>
                <div className="admin-palette-content">
                  {Object.entries(groupedCommands).map(([category, cmds]) => (
                    <div key={category} className="admin-palette-category">
                      <div className="admin-palette-category-title">{category}</div>
                      {cmds.map(cmd => (
                        <button
                          key={cmd.id}
                          className="admin-palette-item"
                          onClick={() => handleCommandSelect(cmd)}
                        >
                          <cmd.icon size={16} />
                          <div className="admin-palette-item-info">
                            <span className="admin-palette-item-name">{cmd.name}</span>
                            <span className="admin-palette-item-desc">{cmd.description}</span>
                          </div>
                          <kbd>{cmd.shortcut}</kbd>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Autocomplete Dropdown */}
            {showAutocomplete && (
              <div className="admin-autocomplete">
                <div className="admin-autocomplete-header">
                  {autocompleteType === 'merchant' && (
                    <>
                      <Store size={14} />
                      <span>Select Merchant</span>
                    </>
                  )}
                  {autocompleteType === 'subscription' && (
                    <>
                      <CreditCard size={14} />
                      <span>Select Subscription</span>
                    </>
                  )}
                </div>
                <div className="admin-autocomplete-content">
                  {autocompleteLoading ? (
                    <div className="admin-autocomplete-loading">
                      <Loader2 className="admin-spinner" size={16} />
                      <span>Searching...</span>
                    </div>
                  ) : autocompleteItems.length === 0 ? (
                    <div className="admin-autocomplete-empty">
                      No results found
                    </div>
                  ) : (
                    autocompleteItems.map((item, idx) => (
                      <button
                        key={item.id}
                        className={`admin-autocomplete-item ${idx === selectedAutocompleteIndex ? 'selected' : ''}`}
                        onClick={() => handleAutocompleteSelect(item)}
                      >
                        {autocompleteType === 'merchant' && (
                          <>
                            <div className="admin-autocomplete-main">
                              <span className="admin-autocomplete-name">{item.storeName}</span>
                              <span
                                className="admin-autocomplete-status"
                                style={{ background: getStatusColor(item.status) }}
                              >
                                {item.status}
                              </span>
                            </div>
                            <div className="admin-autocomplete-meta">
                              <span>{item.email}</span>
                              <span>{item.productCount} products • {item.orderCount} orders</span>
                            </div>
                          </>
                        )}
                        {autocompleteType === 'subscription' && (
                          <>
                            <div className="admin-autocomplete-main">
                              <span className="admin-autocomplete-name">{item.storeName || item.id.slice(-8)}</span>
                              <span
                                className="admin-autocomplete-status"
                                style={{ background: getStatusColor(item.status) }}
                              >
                                {item.status}
                              </span>
                            </div>
                            <div className="admin-autocomplete-meta">
                              <span>{item.tierName}</span>
                              <span>{formatPlatformMoney(item.amount)} / {item.billingCycle?.toLowerCase()}</span>
                            </div>
                          </>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Quick action triggers */}
            <div className="admin-input-triggers">
              <button
                className="admin-trigger-btn"
                onClick={() => handleQuickAction('@')}
                title="Search merchants"
              >
                <AtSign size={16} />
              </button>
              <button
                className="admin-trigger-btn"
                onClick={() => handleQuickAction('#')}
                title="Search subscriptions"
              >
                <Hash size={16} />
              </button>
            </div>

            <div className="admin-input-container">
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything, use @ for merchants, # for subscriptions, or / for commands..."
                className="admin-input"
                rows="1"
                disabled={loading}
              />
              <button
                onClick={() => handleSend()}
                disabled={loading || !input.trim()}
                className="admin-send-btn"
              >
                {loading ? <Loader2 className="admin-spinner" size={20} /> : <Send size={20} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
